import React from 'react';
import FolderCard, { EXPANDED_BODY_BG } from './FolderCard';
import FannedStackBase from './FannedStackBase';
import { formatTotalDuration } from '../../utils/dateHelpers';

/**
 * FannedStack — "fanned archive" renderer for completed FILE FOLDERS.
 *
 * Every file starts as a collapsed navy tab, stacked with a small
 * overlap so the column reads like a physical pile of manila folders.
 * Hovering (or keyboard-focusing, or tapping on touch devices) any tab
 * expands it inline into the standard FolderCard. The expanded card
 * uses the normal light body — it is **visually identical** to the
 * file cards in every other column; the fanned archive only changes
 * how cards are stacked when collapsed, never their appearance when
 * they're revealed.
 *
 * The stacking MECHANIC (hover-intent timing, exclusive expansion,
 * touch protocol, reduced motion, the stable per-item wrapper) lives in
 * FannedStackBase and is shared with the completed-prep-request stack
 * (PrepFannedStack). This file owns only the file-folder PRESENTATION:
 * the navy CollapsedTab and its right-side indicator.
 *
 * Props:
 *   files              Array of file records for this column.
 *   notesCountFor(f)   Returns the notes count to show for a given file.
 *                      Lets callers suppress counts per their own rules
 *                      (e.g. the assistant view hides notes for files
 *                      still in `submitted`).
 *   onFileClick(f)     Called when the user commits to opening the file
 *                      (desktop: any click; touch: a tap on the already-
 *                      expanded card).
 *   emptyMessage       Text to show when `files` is empty.
 *   emptyStyle         Inline style object for the empty-state element.
 *   completedView      When true the right-side tab indicator and the
 *                      expanded FolderCard switch to "completed-column"
 *                      mode: total completion time replaces URG / notes
 *                      / days-in-column, and the expanded card hides
 *                      the URGENT pill and notes badge.
 *   interactive        App-level role gate (CLAUDE.md §30): when false the
 *                      tabs drop the pointer cursor + button semantics and
 *                      the hover/tap expand affordance is suppressed.
 */
export default function FannedStack({
  files,
  notesCountFor = () => 0,
  onFileClick,
  emptyMessage = 'No files',
  emptyStyle,
  completedView = false,
  interactive = true,
}) {
  return (
    <FannedStackBase
      items={files}
      idOf={(f) => f.id}
      onItemOpen={onFileClick}
      emptyMessage={emptyMessage}
      emptyStyle={emptyStyle}
      interactive={interactive}
      renderExpanded={(file, { onClick }) => (
        <FolderCard
          file={file}
          notesCount={notesCountFor(file) || 0}
          completedView={completedView}
          onClick={onClick}
        />
      )}
      renderCollapsed={(file, { onClick, focusBind, reducedMotion }) => (
        <CollapsedTab
          file={file}
          notesCount={notesCountFor(file) || 0}
          reducedMotion={reducedMotion}
          completedView={completedView}
          interactive={interactive}
          onClick={onClick}
          focusBind={focusBind}
        />
      )}
    />
  );
}

function CollapsedTab({ file, notesCount, reducedMotion, completedView, interactive = true, onClick, focusBind }) {
  const indicator = getTabIndicator(file, notesCount, completedView);
  const displayName = truncate(file.clientName || '', 22);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  // Non-permitted role: drop the button semantics + hover/focus expand binds
  // and the pointer cursor so the tab reads as non-interactive (§30). onClick
  // still fires so the pipeline can toast the friendly message.
  const interactiveBind = interactive
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Expand file details for ${file.clientName}`,
        onKeyDown: handleKeyDown,
        ...focusBind,
      }
    : {};

  return (
    <div
      onClick={onClick}
      {...interactiveBind}
      // Return-to-dashboard lookup (CLAUDE.md §39) — collapsed tabs carry
      // the same identity as the full card so an archived file can still be
      // found and highlighted.
      data-card-type="file"
      data-card-id={file.id}
      style={{
        height: '40px',
        minHeight: '40px',
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        background: EXPANDED_BODY_BG,
        color: '#FFFFFF',
        borderRadius: '9px 9px 3px 3px',
        boxShadow: '0 -2px 6px rgba(26, 37, 64, 0.08)',
        cursor: interactive ? 'pointer' : 'default',
        fontWeight: 700,
        fontSize: '13px',
        lineHeight: 1.2,
        userSelect: 'none',
        outline: 'none',
        transition: reducedMotion
          ? 'none'
          : 'box-shadow 200ms ease-out',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: '1 1 auto',
        }}
      >
        {displayName}
      </span>
      <span
        style={{
          flex: '0 0 auto',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          color: indicator.color,
          opacity: indicator.opacity,
          whiteSpace: 'nowrap',
        }}
      >
        {indicator.text}
      </span>
    </div>
  );
}

/**
 * Priority-ordered indicator shown on the right of a collapsed tab.
 *
 * In the File Completed column (`completedView === true`) the slot is
 * reserved exclusively for the file's total completion time
 * (`completed_at − submitted_at`). The URG / notes / days fallbacks
 * are skipped — completed files don't carry any of those signals.
 *
 * Otherwise:
 *   1. "URG" (coral) if the file is urgent
 *   2. "<n> ●" if the file has notes
 *   3. "<days>d" otherwise (falls back to submittedAt-based elapsed days)
 */
function getTabIndicator(file, notesCount, completedView) {
  if (completedView) {
    return {
      text: formatTotalDuration(completionDurationMs(file)),
      color: '#FFFFFF',
      opacity: 0.7,
    };
  }
  if (file.priority === 'Urgent') {
    return { text: 'URG', color: '#F4A89B', opacity: 1 };
  }
  if (notesCount > 0) {
    return { text: `${notesCount} ●`, color: '#FFFFFF', opacity: 0.8 };
  }
  const days = resolveDaysInColumn(file);
  if (days != null) {
    return { text: `${days.toFixed(1)}d`, color: '#FFFFFF', opacity: 0.55 };
  }
  return { text: '', color: '#FFFFFF', opacity: 0.55 };
}

function completionDurationMs(file) {
  if (!file.completedAt || !file.submittedAt) return null;
  const start = new Date(file.submittedAt).getTime();
  const end = new Date(file.completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return end - start;
}

function resolveDaysInColumn(file) {
  if (typeof file.activeDaysUsed === 'number' && file.activeDaysUsed > 0) {
    return file.activeDaysUsed;
  }
  const anchor = file.submittedAt ? new Date(file.submittedAt) : null;
  if (!anchor || Number.isNaN(anchor.getTime())) return null;
  const elapsedMs = Date.now() - anchor.getTime();
  if (elapsedMs < 0) return null;
  return elapsedMs / (1000 * 60 * 60 * 24);
}

function truncate(text, max) {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

// Re-exported for consistency with the constant's definition site.
export { EXPANDED_BODY_BG };
